import { gql, useApolloClient } from '@apollo/client'
import type { MutationUpdaterFunction } from '@apollo/client/core/types'
import type { ApolloError } from '@apollo/client/errors'

import { useUserContext } from '@/contexts/UserContext.tsx'
import { getMutation } from '@/requests/apollo/mutation.ts'
import { STORY_COMMAND_META } from '@/requests/apollo/schema.ts'
import { getExecuteTemplate } from '@/requests/apollo/templates.ts'
import { useQueryProjectMeta } from '@/requests/queries/useQueryProject.ts'
import { useQueryStory } from '@/requests/queries/useQueryStory.ts'
import { isTemporaryId } from '@/utils/key.ts'

export const useMutateStory = (id: string) => {
  // biome-ignore lint/style/noNonNullAssertion: This is guaranteed to be non-null
  const user = useUserContext().user!
  const { projectMeta } = useQueryProjectMeta()
  const { story } = useQueryStory(id)
  const { cache } = useApolloClient()

  const [execute] = getMutation('Story', STORY_COMMAND_META)

  return {
    mutate: (
      parameters: Partial<Story>,
      options?: {
        // biome-ignore lint/suspicious/noExplicitAny:
        update?: MutationUpdaterFunction<any, any, any, any>
        onError?: (error: ApolloError) => void
        onCompleted?: () => void
        optimisticResponseParameters?: Partial<Story>
      },
    ) => {
      if (isTemporaryId(id)) {
        cache.writeFragment({
          id: cache.identify({ __typename: 'Story', id }),
          fragment: gql`
            fragment UpdateStory${Object.keys(parameters).join('\n')} on Story {
              ${Object.keys(parameters).join('\n')}
            }`,
          data: {
            ...parameters,
            id,
          },
        })
        return Promise.resolve()
      }

      const optimisticResponseParameters = options?.optimisticResponseParameters
        ? options?.optimisticResponseParameters
        : parameters
      const { variables, optimisticResponse } = getExecuteTemplate(
        projectMeta,
        'STORY_UPDATE',
        user.id,
        {
          ...parameters,
          id,
        },
        {
          ...story,
          ...optimisticResponseParameters,
          __typename: 'Story',
        },
      )

      return execute({
        variables,
        optimisticResponse,
        update: options?.update,
        onError: options?.onError,
        onCompleted: options?.onCompleted,
      })
    },
  }
}
